import { ActivityCode, Content, InlineResponse2001, InterestCode, SearchType } from 'pleinchamp-api-client';
import { useEffect, useRef, useState } from 'react';
import { PlcApiResponseInterface, usePlcApiCall } from '@api/business/api.hook';
import { Constants } from '@utils/Constants';
import { usePlcRouter } from '@utils/customHooks';
import { ExtendedPagination, extendPagination } from '@utils/pagination';
import { formatUrlWithParam, urlEnum, UrlParams } from '@utils/url';
import { activityInUrl, contentTypeInUrl, interestInUrl, SearchContentType } from './search.types';
import { getSearchContentTypes, searchContent, SearchParams } from './search.utils';

export function useSearchRouteQuery(
  interests: InterestCode[],
  activities: ActivityCode[],
  contentType: SearchContentType,
  searchQuery?: string,
  pageNb?: number
) {
  const { replace, pathname, push } = usePlcRouter();
  const firstRender = useRef(false);
  useEffect(() => {
    if (!firstRender.current) {
      firstRender.current = true;
      return;
    }
    const routeQuery: UrlParams = {
      activites: activities.map(v => activityInUrl[v]),
      centres_d_interets: interests.map(v => interestInUrl[v]),
      mots_cles: searchQuery === '' ? undefined : searchQuery,
      page: pageNb ? pageNb.toString() : undefined,
      type_de_contenu: contentTypeInUrl[contentType],
    };

    if (pathname === urlEnum.search) {
      replace(
        {
          pathname: urlEnum.search,
          query: routeQuery,
        },
        formatUrlWithParam(urlEnum.search, routeQuery),
        {
          shallow: true,
        }
      );
    } else {
      push(formatUrlWithParam(urlEnum.search, routeQuery));
    }
  }, [searchQuery, interests, activities, contentType, pageNb]);
}

export function fetchResults(params: Omit<SearchParams, 'contentTypes'> & { contentType?: SearchContentType }) {
  const {
    activities,
    contentType,
    interests,
    limit = Constants.SEARCH_RESULT_PER_PAGE[1],
    offset = 0,
    query,
    searchType = SearchType.Prioritize,
  } = params;
  return searchContent({
    activities,
    contentTypes: getSearchContentTypes(contentType),
    interests,
    limit,
    offset,
    query,
    searchType,
  });
}

type SearchFetchingParams = {
  defaultOffset: number;
  setOffset: (newOffset: number) => void;
  activities: ActivityCode[];
  contentType: SearchContentType;
  interests: InterestCode[];
  offset: number;
  query?: string;
  initialData?: InlineResponse2001;
  limit: number;
};

type SearchFetchingResults = {
  data?: InlineResponse2001;
  fetchNextResults: () => void;
  isNextResultsValidating: boolean;
  pagination?: ExtendedPagination;
  results: Content[];
  searchResults: PlcApiResponseInterface<InlineResponse2001>;
};

export function useSearchFetching({
  defaultOffset,
  setOffset,
  activities,
  contentType,
  interests,
  offset,
  query,
  initialData,
  limit,
}: SearchFetchingParams): SearchFetchingResults {
  const { pathname } = usePlcRouter();
  const [results, setResults] = useState(initialData?.contents ?? []);
  const [pagination, setPagination] = useState(initialData ? extendPagination(initialData.pagination) : undefined);
  const [isNextResultsValidating, setIsNextResultsValidating] = useState(false);

  const searchResults = usePlcApiCall(fetchResults, [{ activities, contentType, interests, limit, offset, query }], {
    // do not call api if we are on interest or activity page
    enabled: pathname === urlEnum.search,
    initialData,
  });

  const { data } = searchResults;

  useEffect(() => {
    if (data) {
      const { pagination: nextPagination, contents } = data;
      const resultsSliceLimit = nextPagination.offset - defaultOffset;
      setResults(results.slice(0, resultsSliceLimit).concat(contents));
      setPagination(extendPagination(nextPagination));
    }
    setIsNextResultsValidating(false);
  }, [data]);

  function fetchNextResults() {
    if (pagination?.hasNext) {
      setIsNextResultsValidating(true);
      setOffset(offset + limit);
    }
  }

  return {
    data,
    fetchNextResults,
    isNextResultsValidating,
    pagination,
    results,
    searchResults,
  };
}
